import React, { useMemo } from 'react';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ComposedChart, Line,
} from 'recharts';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { PeriodConfig, formatNumber, formatDate, formatMonth, tooltipStyle } from '../types';

interface HomeTabProps {
  period: PeriodConfig;
}

const ENDPOINTS = [
  'overview',
  'signups-daily',
  'signups-monthly',
  'signups-hourly-5d',
  'countries',
  'revenue-dashboard',
  'ad-spend-dashboard',
  'videos-overview',
  'video-status-breakdown',
  'videos-monthly',
  'videos-daily',
  'videos-hourly-5d',
  'creators-monthly',
  // D. Interactions
  'engagement-daily',
  'engagement-hourly',
  'messages-daily',
  'watches-minutely-5h',
  // E. SignUps
  'signups-by-type-daily',
  'signups-by-type-hourly-5d',
  // F. Retention
  'retention-dau-30d',
  'retention-mau-12m',
  'retention-cohorts',
];

const fmtUSD = (n: number) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtUSD2 = (n: number) => `$${(n || 0).toFixed(2)}`;
// Compact form for dense per-channel cells: whole dollars, and an em-dash for
// a true zero so an unused channel reads as "not running" rather than "$0 spent".
const fmtUSD0 = (n: number) => (n ? `$${Math.round(n).toLocaleString()}` : '—');
// Daily cards cover whole UTC days from the earlier of "30 days ago" (today
// counts as one) and the 1st of this month, so on the 31st the month is shown
// whole. Mirrors dailyWindowStart() in server/src/services/analyticsWorker.ts.
const dailyWindow = (): { from: string; month: string; label: string } => {
  const now = new Date();
  const y = now.getUTCFullYear(), m = now.getUTCMonth(), d = now.getUTCDate();
  const from = new Date(Math.min(Date.UTC(y, m, d - 29), Date.UTC(y, m, 1)));
  const fmt = (x: Date, opts: Intl.DateTimeFormatOptions) =>
    x.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
  const label = from.getUTCMonth() === m
    ? `${fmt(from, { month: 'short', day: 'numeric' })}–${d}`
    : `${fmt(from, { month: 'short', day: 'numeric' })} – ${fmt(now, { month: 'short', day: 'numeric' })}`;
  return { from: from.toISOString().slice(0, 10), month: now.toISOString().slice(0, 7), label };
};
const inWindow = (rows: any[], from: string) =>
  (rows || []).filter((r: any) => String(r.date || '').slice(0, 10) >= from);

const pctChange = (curr: number, prev: number): { text: string; positive: boolean } => {
  if (!prev) return { text: curr > 0 ? '+∞%' : '0%', positive: curr >= 0 };
  const p = ((curr - prev) / prev) * 100;
  return { text: `${p >= 0 ? '+' : ''}${p.toFixed(1)}%`, positive: p >= 0 };
};

const Delta: React.FC<{ label: string; curr: number; prev: number; isCurrency?: boolean }> = ({ label, curr, prev, isCurrency }) => {
  const c = pctChange(curr, prev);
  return (
    <div className="text-xs">
      <div className="flex items-center justify-between">
        <span className="text-gray-400">{label}</span>
        <span className={`px-1.5 py-0.5 rounded ${c.positive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
          {c.text}
        </span>
      </div>
      <div className="text-gray-300 font-mono mt-0.5">
        {isCurrency ? fmtUSD(curr) : (curr || 0).toLocaleString()}
        <span className="text-gray-600"> vs {isCurrency ? fmtUSD(prev) : (prev || 0).toLocaleString()}</span>
      </div>
    </div>
  );
};

const HomeTab: React.FC<HomeTabProps> = ({ period }) => {
  const { data, loading } = useAnalyticsData(ENDPOINTS, period);

  const overview = data['overview'];
  const rev = useMemo(() => data['revenue-dashboard'] || {}, [data]);
  const ads = useMemo(() => data['ad-spend-dashboard'] || {}, [data]);

  // TikTok is approved but not yet OAuth'd, so it reports 0. Rendering a
  // permanently-empty column/series just adds noise — only show it once real
  // spend exists anywhere in the visible windows.
  const adsHasTiktok = useMemo(() => {
    const inDaily = (ads.dailySpend || []).some((d: any) => (d.tiktok || 0) > 0);
    const inMonthly = (ads.monthlySpend || []).some((m: any) => (m.tiktok || 0) > 0);
    return inDaily || inMonthly || (ads.monthByChannel?.tiktok || 0) > 0;
  }, [ads]);

  const adsChannels = useMemo(() => {
    const by = ads.monthByChannel || {};
    const rows = [
      { key: 'facebook', label: 'Facebook', color: '#1877F2', value: by.facebook || 0 },
      { key: 'google', label: 'Google', color: '#EA4335', value: by.google || 0 },
      { key: 'tiktok', label: 'TikTok', color: '#22d3ee', value: by.tiktok || 0 },
    ].filter(r => r.key !== 'tiktok' || adsHasTiktok);
    const total = rows.reduce((s, r) => s + r.value, 0);
    return rows.map(r => ({ ...r, pct: total > 0 ? ((r.value / total) * 100).toFixed(1) : '0.0' }));
  }, [ads, adsHasTiktok]);
  const videosOverview = data['videos-overview'];
  const countries = useMemo<any[]>(() => data['countries'] || [], [data]);
  const statusBreakdown = useMemo<any[]>(() => data['video-status-breakdown'] || [], [data]);
  // Recomputed on every refresh so the window moves at UTC midnight.
  const win = useMemo(() => dailyWindow(), [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const signupsLast30 = useMemo(() => {
    // signups-daily returns 90 whole days; show the daily window.
    return inWindow(data['signups-daily'], win.from).map((d: any) => ({
      ...d,
      label: formatDate(d.date),
      rawDate: (d.date || '').slice(0, 10),
    }));
  }, [data, win]);
  // signups-hourly-5d returns up to 120 hours (5 days). Slicing to 72h
  // gives a 3-day rolling hourly view while still fitting the compact card.
  const signupsHourly72 = useMemo(() => {
    const all = data['signups-hourly-5d'] || [];
    return all.slice(-72).map((d: any) => ({ ...d, label: `${d.date.slice(5)} ${d.hour}:00` }));
  }, [data]);

  const videosDaily30 = useMemo(() => {
    return inWindow(data['videos-daily'], win.from).map((d: any) => ({ ...d, label: formatDate(d.date) }));
  }, [data, win]);
  const videosHourly48 = useMemo(() => {
    const all = data['videos-hourly-5d'] || [];
    return all.slice(-48).map((d: any) => ({ ...d, label: `${d.date.slice(5)} ${d.hour}:00` }));
  }, [data]);
  const videosMonthlyMerged = useMemo(() => {
    const vm = data['videos-monthly'] || [];
    const cm = data['creators-monthly'] || [];
    // Pair by YYYY-MM key — the two arrays are NOT index-aligned because
    // videos-monthly is zero-filled from MONTHLY_START while creators-monthly
    // skips months that had no uploads. Merging by index produced a 2-month
    // drift (Jan 26 videos paired with Mar 26 creators, etc.).
    const creatorsByMonth: Record<string, { creatorCount: number; videoCount: number }> = {};
    for (const c of cm) {
      if (c?.month) creatorsByMonth[c.month] = c;
    }
    return vm.map((d: any) => {
      const ymKey = `${d.year}-${String(d.month).padStart(2, '0')}`;
      const creators = creatorsByMonth[ymKey]?.creatorCount || 0;
      const videos = d.count || 0;
      return {
        label: formatMonth(d.year, d.month),
        videos,
        creators,
        avgPerUser: creators > 0 ? +(videos / creators).toFixed(2) : 0,
      };
    });
  }, [data]);

  // 30d vs prev 30d from signups-daily.
  const { month30Curr, month30Prev } = useMemo(() => {
    const all = (data['signups-daily'] || []).filter((d: any) => (d.count || 0) > 0);
    const sum = (rows: any[]) => rows.reduce((acc: number, r: any) => acc + (r.count || 0), 0);
    const last30 = all.slice(-30);
    const prev30 = all.slice(-60, -30);
    return { month30Curr: sum(last30), month30Prev: sum(prev30) };
  }, [data]);
  const dayCurr = overview?.todaySignups || 0;
  // Prefer the time-matched yesterday count (yesterday up to now-24h) for
  // an apples-to-apples "same hour yesterday" comparison. Fall back to the
  // full 24h yesterday count if the backend hasn't shipped the new field yet.
  const dayPrev = overview?.yesterdaySignupsSameTime ?? overview?.yesterdaySignups ?? 0;
  const dayPrevIsSameTime = overview?.yesterdaySignupsSameTime !== undefined;

  const sortedCountries = useMemo(() => [...countries].sort((a: any, b: any) => b.count - a.count), [countries]);

  const sortedStatusAlpha = useMemo(
    () => [...statusBreakdown].sort((a: any, b: any) => String(a.status).localeCompare(String(b.status))),
    [statusBreakdown],
  );
  const todayDailyRow = (rev.dailyRevenue || []).slice(-1)[0] || {};

  // Trial totals — today from the trialToday map, this month (calendar month,
  // UTC) from the server; older servers only sent dailyRevenue, so sum this
  // month's rows of it instead.
  const trialMap: Record<string, { count: number; revenue: number }> = useMemo(() => {
    const m: Record<string, { count: number; revenue: number }> = {};
    for (const t of (rev.trialToday || [])) m[t.type] = { count: t.count || 0, revenue: t.revenue || 0 };
    return m;
  }, [rev]);
  const todayTrialStarts = trialMap['INITIAL_PURCHASE']?.count || 0;
  const todayTrialConverted = trialMap['RENEWAL']?.count || 0;
  const todayTrialConvertedRev = trialMap['RENEWAL']?.revenue || 0;
  const monthTrialStarts = useMemo(
    () => rev.monthTrialStarts ?? (rev.dailyRevenue || [])
      .filter((r: any) => String(r.date || '').startsWith(win.month))
      .reduce((s: number, r: any) => s + (r.trialStarts || 0), 0),
    [rev, win],
  );
  const monthTrialConverted = useMemo(
    () => rev.monthTrialConverted ?? (rev.dailyRevenue || [])
      .filter((r: any) => String(r.date || '').startsWith(win.month))
      .reduce((s: number, r: any) => s + (r.trialRenewals || 0), 0),
    [rev, win],
  );

  // ─── D. Interactions data shaping ────────────────────────
  // engagement-daily payload shape: { watches: [{date,count}], likes: [...], ... }
  const interactionsDaily30 = useMemo(() => {
    const ed = data['engagement-daily'] || {};
    const shape = (arr: any[]) =>
      inWindow(arr, win.from).map((d: any) => ({
        ...d,
        label: formatDate(d.date),
        rawDate: (d.date || '').slice(0, 10),
      }));
    return {
      watches: shape(ed.watches),
      likes: shape(ed.likes),
      comments: shape(ed.comments),
      shares: shape(ed.shares),
      saves: shape(ed.saves),
    };
  }, [data, win]);

  // engagement-hourly payload shape: { watches: [{date,hour,count}], ... }
  const interactionsHourly48 = useMemo(() => {
    const eh = data['engagement-hourly'] || {};
    const shape = (arr: any[]) =>
      (arr || []).slice(-48).map((d: any) => ({
        ...d,
        label: `${d.date.slice(5)} ${d.hour}:00`,
      }));
    return {
      watches: shape(eh.watches),
      likes: shape(eh.likes),
      comments: shape(eh.comments),
      shares: shape(eh.shares),
      saves: shape(eh.saves),
    };
  }, [data]);

  // watches-minutely-5h: array of { date, hour, minute, count } — last 5h
  const watchesMinutely = useMemo(() => {
    const rows = data['watches-minutely-5h'] || [];
    return rows.map((d: any) => ({
      ...d,
      label: `${String(d.hour).padStart(2, '0')}:${String(d.minute).padStart(2, '0')}`,
    }));
  }, [data]);

  // messages-daily payload shape: { dm: [{date,count}], group: [...], hub: [...], oneToOne: [...] }
  const messagesDaily30 = useMemo(() => {
    const md = data['messages-daily'] || {};
    const shape = (arr: any[]) =>
      inWindow(arr, win.from).map((d: any) => ({
        ...d,
        label: formatDate(d.date),
        rawDate: (d.date || '').slice(0, 10),
      }));
    return {
      dm: shape(md.dm),
      group: shape(md.group),
      hub: shape(md.hub),
      oneToOne: shape(md.oneToOne),
    };
  }, [data, win]);

  // signups-by-type-daily: { google: [{date,count}], facebook: [...], phone: [...], email: [...], appleOther: [...] }
  const signupsByTypeDaily30 = useMemo(() => {
    const sbt = data['signups-by-type-daily'] || {};
    const shape = (arr: any[]) =>
      inWindow(arr, win.from).map((d: any) => ({
        ...d,
        label: formatDate(d.date),
        rawDate: (d.date || '').slice(0, 10),
      }));
    return {
      google: shape(sbt.google),
      facebook: shape(sbt.facebook),
      phone: shape(sbt.phone),
      email: shape(sbt.email),
      appleOther: shape(sbt.appleOther),
    };
  }, [data, win]);

  // signups-by-type-hourly-5d: same keys, each [{date,hour,count}]
  const signupsByTypeHourly48 = useMemo(() => {
    const sbt = data['signups-by-type-hourly-5d'] || {};
    const shape = (arr: any[]) =>
      (arr || []).slice(-48).map((d: any) => ({
        ...d,
        label: `${d.date.slice(5)} ${d.hour}:00`,
      }));
    return {
      google: shape(sbt.google),
      facebook: shape(sbt.facebook),
      phone: shape(sbt.phone),
      email: shape(sbt.email),
      appleOther: shape(sbt.appleOther),
    };
  }, [data]);

  // retention-dau-30d: [{date, dau}]
  const retentionDau30 = useMemo(() => {
    return inWindow(data['retention-dau-30d'], win.from).map((d: any) => ({
      ...d,
      label: formatDate(d.date),
      rawDate: (d.date || '').slice(0, 10),
    }));
  }, [data, win]);

  // retention-mau-12m: [{month, mau}]
  const retentionMau12 = useMemo(() => {
    return (data['retention-mau-12m'] || []).map((d: any) => {
      const [y, m] = (d.month || '').split('-').map(Number);
      return {
        ...d,
        label: formatMonth(y, m),
      };
    });
  }, [data]);

  // retention-cohorts: [{date, cohortSize, d1Rate, d7Rate, d30Rate}] — Recharts
  // drops points with explicit null, so the three series render only where
  // their cohort has matured far enough.
  const retentionCohorts = useMemo(() => {
    return (data['retention-cohorts'] || []).map((d: any) => ({
      ...d,
      label: formatDate(d.date),
      rawDate: (d.date || '').slice(0, 10),
    }));
  }, [data]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-5">
      {/* ============ A. USER ANALYTICS ============ */}
      <section>
        <h2 className="text-lg font-bold text-white mb-2">A. Users</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-3 sm:gap-4">
          {/* A1 — Two stacked sections: total users + today's signups */}
          <div className="xl:col-span-2 bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col h-full">
            {/* Top section — total active registered users */}
            <div>
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Active Registered Users</p>
              <p className="text-3xl font-bold text-white mt-1 leading-tight">
                {(overview?.totalUsers || 0).toLocaleString()}
              </p>
              <div className="mt-2">
                <Delta label="Last 30d vs Prev 30d" curr={month30Curr} prev={month30Prev} />
              </div>
            </div>

            {/* Divider */}
            <div className="my-3 border-t border-white/10" />

            {/* Bottom section — today's signups with yesterday comparison */}
            <div>
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Today's Signups</p>
              <p className="text-3xl font-bold text-white mt-1 leading-tight">
                {dayCurr.toLocaleString()}
              </p>
              <div className="mt-2">
                <Delta
                  label={dayPrevIsSameTime ? 'Today vs Yesterday (same time)' : 'Today vs Yesterday'}
                  curr={dayCurr}
                  prev={dayPrev}
                />
              </div>
            </div>
          </div>

          {/* A2 — Daily signups last 30d (compact) */}
          <div className="xl:col-span-3 h-full">
            <ChartCard title={`Daily Signups (${win.label})`}>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={signupsLast30}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" fill="#FF9C15" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* A3 — Hourly past 48h */}
          <div className="xl:col-span-3 h-full">
            <ChartCard title="Hourly Signups (72h)">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={signupsHourly72}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={8} tickLine={false} interval="preserveStartEnd" />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" fill="#fb923c" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* A5 — Last 30 days table */}
          <div className="xl:col-span-2 h-full">
            <ChartCard title={`Daily Signups — List (${win.label})`}>
              <div className="max-h-[200px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[#0b0d14]">
                    <tr className="border-b border-white/10">
                      <th className="text-left text-gray-400 py-1 px-2">Date</th>
                      <th className="text-right text-gray-400 py-1 px-2">Signups</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...signupsLast30].reverse().map((d: any, i: number) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="text-gray-300 py-1 px-2 font-mono">{d.rawDate}</td>
                        <td className="text-right text-white py-1 px-2 font-mono">{(d.count || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          </div>

          {/* A4 — Countries (exact numbers, no bars) */}
          <div className="xl:col-span-2 h-full">
            <ChartCard title={`Countries — ${overview?.uniqueCountries || sortedCountries.length} unique`}>
              <div className="max-h-[200px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[#0b0d14]">
                    <tr className="border-b border-white/10">
                      <th className="text-left text-gray-400 py-1 px-2">#</th>
                      <th className="text-left text-gray-400 py-1 px-2">Country</th>
                      <th className="text-right text-gray-400 py-1 px-2">Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCountries.map((c: any, i: number) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="text-gray-500 py-1 px-2 font-mono">{i + 1}</td>
                        <td className="text-white py-1 px-2 truncate max-w-[120px]">{c.country}</td>
                        <td className="text-right text-white py-1 px-2 font-mono">{(c.count || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          </div>
        </div>
      </section>

      {/* ============ B. REVENUE ============ */}
      <section>
        <h2 className="text-lg font-bold text-white mb-2">B. Revenue</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-3 sm:gap-4">
          {/* B1 — Today */}
          <div className="xl:col-span-2 bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col h-full">
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Today Revenue</p>
            <p className="text-3xl font-bold text-emerald-400 mt-1 leading-tight">{fmtUSD2(rev.todayRevenue || 0)}</p>
            <div className="grid grid-cols-2 gap-2 mt-3 border-t border-white/10 pt-3">
              <div><p className="text-[9px] text-gray-500 uppercase">Subs</p><p className="text-xs text-white font-mono">{fmtUSD(todayDailyRow.subs || 0)}</p></div>
              <div><p className="text-[9px] text-gray-500 uppercase">Coins</p><p className="text-xs text-white font-mono">{fmtUSD(todayDailyRow.coins || 0)}</p></div>
              <div><p className="text-[9px] text-gray-500 uppercase">Tokens</p><p className="text-xs text-white font-mono">{fmtUSD(todayDailyRow.diamonds || 0)}</p></div>
              <div><p className="text-[9px] text-gray-500 uppercase">Streak</p><p className="text-xs text-white font-mono">{fmtUSD(todayDailyRow.streak || 0)}</p></div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2 border-t border-white/10 pt-2">
              <div><p className="text-[9px] text-gray-500 uppercase" title="Distinct people who started a trial today (re-trials by the same user count once). RevenueCat counts transactions, so it reads higher.">Trial Starts (people)</p><p className="text-xs text-purple-300 font-mono">{todayTrialStarts.toLocaleString()}</p></div>
              <div><p className="text-[9px] text-gray-500 uppercase">Converted</p><p className="text-xs text-emerald-300 font-mono">{todayTrialConverted.toLocaleString()} <span className="text-gray-500">({fmtUSD(todayTrialConvertedRev)})</span></p></div>
            </div>
          </div>

          {/* B2 — Month */}
          <div className="xl:col-span-2 bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col h-full">
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">This Month Revenue</p>
            <p className="text-3xl font-bold text-emerald-400 mt-1 leading-tight">{fmtUSD2(rev.monthRevenue || 0)}</p>
            <div className="grid grid-cols-2 gap-2 mt-3 border-t border-white/10 pt-3">
              <div><p className="text-[9px] text-gray-500 uppercase">Subs</p><p className="text-xs text-white font-mono">{fmtUSD(rev.monthSubs || 0)}</p></div>
              <div><p className="text-[9px] text-gray-500 uppercase">Coins</p><p className="text-xs text-white font-mono">{fmtUSD(rev.monthCoins || 0)}</p></div>
              <div><p className="text-[9px] text-gray-500 uppercase">Tokens</p><p className="text-xs text-white font-mono">{fmtUSD(rev.monthDiamonds || 0)}</p></div>
              <div><p className="text-[9px] text-gray-500 uppercase">Streak</p><p className="text-xs text-white font-mono">{fmtUSD(rev.monthStreak || 0)}</p></div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2 border-t border-white/10 pt-2">
              <div><p className="text-[9px] text-gray-500 uppercase">Trial Starts</p><p className="text-xs text-purple-300 font-mono">{monthTrialStarts.toLocaleString()}</p></div>
              <div><p className="text-[9px] text-gray-500 uppercase">Converted</p><p className="text-xs text-emerald-300 font-mono">{monthTrialConverted.toLocaleString()}</p></div>
            </div>
          </div>

          {/* B3 — Daily revenue 30d bar + trial-starts trendline */}
          <div className="xl:col-span-4 h-full">
            <ChartCard title={`Daily Revenue (${win.label})`}>
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={rev.dailyRevenue || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={9} tickLine={false} tickFormatter={(v: string) => v.slice(5)} />
                  {/* Left axis — revenue in USD (primary, bars) */}
                  <YAxis yAxisId="left" stroke="#64748b" fontSize={10} tickLine={false} width={40} tickFormatter={(v: number) => `$${v}`} />
                  {/* Right axis — trial starts count (trendline overlay) */}
                  <YAxis yAxisId="right" orientation="right" stroke="#c084fc" fontSize={10} tickLine={false} axisLine={false} width={36} tickFormatter={(v: number) => formatNumber(v)} />
                  <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload || {};
                    return (
                      <div style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#fff' }}>
                        <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
                        <p style={{ color: '#4ade80' }}>Subs: {fmtUSD2(d.subs || 0)}</p>
                        <p style={{ color: '#FF9C15' }}>Coins: {fmtUSD2(d.coins || 0)}</p>
                        <p style={{ color: '#c084fc' }}>Tokens: {fmtUSD2(d.diamonds || 0)}</p>
                        <p style={{ color: '#38bdf8' }}>Streak: {fmtUSD2(d.streak || 0)}</p>
                        <p style={{ fontWeight: 700, marginTop: 4, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 4 }}>Total: {fmtUSD2(d.total || 0)}</p>
                        <div style={{ marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 6 }}>
                          <p style={{ color: '#c084fc' }}>Trial Starts: {(d.trialStarts || 0).toLocaleString()}</p>
                          <p style={{ color: '#38bdf8' }}>Trial → Paid: {(d.trialRenewals || 0).toLocaleString()}</p>
                          <p style={{ color: '#f87171' }}>Cancellations: {(d.allCancels || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    );
                  }} />
                  <Bar yAxisId="left" dataKey="subs" stackId="a" fill="#4ade80" name="Subs" />
                  <Bar yAxisId="left" dataKey="coins" stackId="a" fill="#FF9C15" name="Coins" />
                  <Bar yAxisId="left" dataKey="diamonds" stackId="a" fill="#c084fc" name="Tokens" />
                  <Bar yAxisId="left" dataKey="streak" stackId="a" fill="#38bdf8" name="Streak" />
                  {/* Trial-starts trendline overlay — shows where trials are trending over the month */}
                  <Line yAxisId="right" type="monotone" dataKey="trialStarts" stroke="#c084fc" strokeWidth={2} dot={{ r: 2, fill: '#c084fc' }} activeDot={{ r: 4 }} name="Trial Starts" />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* B4 — Today by Product */}
          <div className="xl:col-span-2 h-full">
            <ChartCard title="Today by Product">
              {(rev.todayProducts || []).length > 0 ? (
                <div className="space-y-1 p-1 max-h-[260px] overflow-y-auto">
                  {(rev.todayProducts || []).map((p: any, i: number) => (
                    <div key={i} className="flex justify-between items-center text-xs gap-2">
                      <span className="text-gray-400 truncate flex-1">{p.product}</span>
                      <span className="text-gray-500 tabular-nums">{p.users} users</span>
                      <span className="text-emerald-400 font-mono tabular-nums">{fmtUSD2(p.revenue)}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-gray-500 text-center py-8 text-xs">No revenue yet today</p>}
            </ChartCard>
          </div>

          {/* B5 — Today by Country */}
          <div className="xl:col-span-2 h-full">
            <ChartCard title="Today by Country">
              {(rev.todayByCountry || []).length > 0 ? (
                <div className="space-y-1 p-1 max-h-[260px] overflow-y-auto">
                  {(rev.todayByCountry || []).map((c: any, i: number) => (
                    <div key={i} className="flex justify-between items-center text-xs gap-2">
                      <span className="text-gray-400 truncate flex-1">{c.country}</span>
                      <span className="text-gray-500 tabular-nums">{c.users} users</span>
                      <span className="text-emerald-400 font-mono tabular-nums">{fmtUSD2(c.revenue)}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-gray-500 text-center py-8 text-xs">No revenue yet today</p>}
            </ChartCard>
          </div>
        </div>

        {/* Second row — B6 daily table, B7 monthly revenue, B8 refunds daily,
            B9 refunds monthly, B10 month-by-country */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 sm:gap-4 mt-3 sm:mt-4">
          {/* B6 — Daily total revenue table (last 30d) */}
          <div className="h-full">
            <ChartCard title={`Daily Revenue — Table (${win.label})`}>
              <div className="max-h-[260px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[#0b0d14]">
                    <tr className="border-b border-white/10">
                      <th className="text-left text-gray-400 py-1 px-2">Date</th>
                      <th className="text-right text-gray-400 py-1 px-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...(rev.dailyRevenue || [])].reverse().map((d: any, i: number) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="text-gray-300 py-1 px-2 font-mono">{d.date}</td>
                        <td className="text-right text-emerald-400 py-1 px-2 font-mono tabular-nums">{fmtUSD2(d.total || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          </div>

          {/* B7 — Monthly revenue bar chart (from Oct 2025) */}
          <div className="h-full">
            <ChartCard title="Monthly Revenue (from Oct 2025)">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={rev.monthlyRevenue || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={9} tickLine={false} tickFormatter={(v: string) => v.slice(5)} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={40} tickFormatter={(v: number) => `$${formatNumber(v)}`} />
                  <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload || {};
                    return (
                      <div style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#fff' }}>
                        <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
                        <p style={{ color: '#4ade80' }}>Subs: {fmtUSD2(d.subs || 0)}</p>
                        <p style={{ color: '#FF9C15' }}>Coins: {fmtUSD2(d.coins || 0)}</p>
                        <p style={{ color: '#c084fc' }}>Tokens: {fmtUSD2(d.diamonds || 0)}</p>
                        <p style={{ color: '#38bdf8' }}>Streak: {fmtUSD2(d.streak || 0)}</p>
                        <p style={{ fontWeight: 700, marginTop: 4, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 4 }}>Total: {fmtUSD2(d.total || 0)}</p>
                        <p style={{ color: '#94a3b8', marginTop: 4 }}>Purchases: {(d.count || 0).toLocaleString()}</p>
                      </div>
                    );
                  }} />
                  <Bar dataKey="subs" stackId="a" fill="#4ade80" name="Subs" />
                  <Bar dataKey="coins" stackId="a" fill="#FF9C15" name="Coins" />
                  <Bar dataKey="diamonds" stackId="a" fill="#c084fc" name="Tokens" />
                  <Bar dataKey="streak" stackId="a" fill="#38bdf8" name="Streak" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* B8 — Refunds daily bar chart (last 30d) */}
          <div className="h-full">
            <ChartCard title={`Refunds — Daily (${win.label})`}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={rev.refundsDaily || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={9} tickLine={false} tickFormatter={(v: string) => (v || '').slice(5)} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={40} tickFormatter={(v: number) => `$${formatNumber(v)}`} />
                  <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload || {};
                    return (
                      <div style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#fff' }}>
                        <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
                        <p style={{ color: '#f87171', fontWeight: 700 }}>Refunded: {fmtUSD2(d.total || 0)}</p>
                        <p style={{ color: '#94a3b8' }}>Count: {d.count || 0}</p>
                      </div>
                    );
                  }} />
                  <Bar dataKey="total" fill="#f87171" name="Refunds" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* B9 — Refunds monthly bar chart (from Oct 2025) */}
          <div className="h-full">
            <ChartCard title="Refunds — Monthly (from Oct 2025)">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={rev.refundsMonthly || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={9} tickLine={false} tickFormatter={(v: string) => (v || '').slice(5)} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={40} tickFormatter={(v: number) => `$${formatNumber(v)}`} />
                  <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload || {};
                    return (
                      <div style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#fff' }}>
                        <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
                        <p style={{ color: '#f87171', fontWeight: 700 }}>Refunded: {fmtUSD2(d.total || 0)}</p>
                        <p style={{ color: '#94a3b8' }}>Count: {d.count || 0}</p>
                      </div>
                    );
                  }} />
                  <Bar dataKey="total" fill="#f87171" name="Refunds" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* B9b — Net Revenue Monthly (gross − refunds, plus estimated payout) */}
          {/*
              Gross alone overstates what we keep — refunds reduce it, and Google's
              ~16% fee+tax cut means actual cash to bank is materially lower.
              This widget joins both into one view per month so anyone reading
              the dashboard can answer "what landed in our account?" at a glance.
              Net revenue (green) = gross − refunds.
              Est. payout (blue)  = net × 0.78 (Play+Apple takehome after
              fees+tax, also corrects RC's catalog-price overstatement of
              ~10% — recalibrated against May 2026 actual payout $34K).
              Tooltip shows all four numbers: gross / refunds / net / payout.
          */}
          <div className="h-full">
            <ChartCard title="Net Revenue (after refunds + fees) — Monthly">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={rev.netRevenueMonthly || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={9} tickLine={false} tickFormatter={(v: string) => (v || '').slice(5)} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={40} tickFormatter={(v: number) => `$${formatNumber(v)}`} />
                  <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload || {};
                    return (
                      <div style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#fff' }}>
                        <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
                        <p style={{ color: '#94a3b8' }}>Gross: {fmtUSD2(d.gross || 0)}</p>
                        <p style={{ color: '#f87171' }}>Refunds: −{fmtUSD2(d.refunds || 0)}</p>
                        <p style={{ color: '#4ade80', fontWeight: 700 }}>Net Revenue: {fmtUSD2(d.netRevenue || 0)}</p>
                        <p style={{ color: '#38bdf8', fontWeight: 700, marginTop: 4, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 4 }}>Est. Payout: {fmtUSD2(d.estimatedPayout || 0)}</p>
                        <p style={{ color: '#64748b', fontSize: 10, marginTop: 4 }}>(net × 0.78 = approx after Play+Apple fees+tax+FX)</p>
                      </div>
                    );
                  }} />
                  <Bar dataKey="netRevenue" fill="#4ade80" name="Net Revenue" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="estimatedPayout" fill="#38bdf8" name="Est. Payout" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* B10 — This month by Country */}
          <div className="h-full">
            <ChartCard title="This Month by Country">
              {(rev.monthByCountry || []).length > 0 ? (
                <div className="space-y-1 p-1 max-h-[260px] overflow-y-auto">
                  {(rev.monthByCountry || []).map((c: any, i: number) => (
                    <div key={i} className="flex justify-between items-center text-xs gap-2">
                      <span className="text-gray-400 truncate flex-1">{c.country}</span>
                      <span className="text-gray-500 tabular-nums">{(c.users || 0).toLocaleString()} users</span>
                      <span className="text-emerald-400 font-mono tabular-nums">{fmtUSD2(c.revenue)}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-gray-500 text-center py-8 text-xs">No revenue yet this month</p>}
            </ChartCard>
          </div>

          {/* ── Ad spend (B11-B14) ──
              These four fill the row B10 left open. Source is
              `dailyCountryAnalytics.adSpend`, the same nightly compute that
              feeds revenue on this page, so spend and revenue on any given
              day are measured over the same window and ROI is a fair ratio.

              IMPORTANT: this is ad DELIVERY cost as Meta/Google report it, not
              what hits the bank. Meta bills at spend thresholds (one card
              charge often covers 2-4 days) and adds GST/VAT on the SGD and INR
              accounts, so statements run ~6-9% higher. A gap against a bank
              statement is expected and is not a bug in these numbers. */}

          {/* B11 — Daily ad spend 30d, stacked by channel, with ROI trendline */}
          <div className="h-full">
            <ChartCard title="Ad Spend — Daily 60d (by channel)">
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={ads.dailySpend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  {/* 60 points in a fifth-of-a-row card: label every 7th day so
                      the axis stays readable instead of collapsing to mush. */}
                  <XAxis dataKey="date" stroke="#64748b" fontSize={9} tickLine={false} interval={6} tickFormatter={(v: string) => (v || '').slice(5)} />
                  <YAxis yAxisId="spend" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={40} tickFormatter={(v: number) => `$${formatNumber(v)}`} />
                  <YAxis yAxisId="roi" orientation="right" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={38} tickFormatter={(v: number) => `${v}%`} />
                  <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload || {};
                    return (
                      <div style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#fff' }}>
                        <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
                        <p style={{ color: '#1877F2' }}>Facebook: {fmtUSD2(d.facebook || 0)}</p>
                        <p style={{ color: '#EA4335' }}>Google: {fmtUSD2(d.google || 0)}</p>
                        {(d.tiktok || 0) > 0 && <p style={{ color: '#22d3ee' }}>TikTok: {fmtUSD2(d.tiktok)}</p>}
                        <p style={{ fontWeight: 700, marginTop: 4, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 4 }}>Total spend: {fmtUSD2(d.total || 0)}</p>
                        <p style={{ color: '#4ade80' }}>Revenue: {fmtUSD2(d.revenue || 0)}</p>
                        <p style={{ color: (d.roi || 0) >= 100 ? '#fbbf24' : '#f87171', fontWeight: 700 }}>ROI: {(d.roi || 0).toFixed(0)}%</p>
                        <p style={{ color: '#94a3b8', marginTop: 4 }}>
                          {(d.installs || 0).toLocaleString()} installs · CPI ${(d.cpi || 0).toFixed(3)}
                        </p>
                      </div>
                    );
                  }} />
                  <Bar yAxisId="spend" dataKey="facebook" stackId="s" fill="#1877F2" name="Facebook" />
                  <Bar yAxisId="spend" dataKey="google" stackId="s" fill="#EA4335" name="Google" />
                  <Bar yAxisId="spend" dataKey="tiktok" stackId="s" fill="#22d3ee" name="TikTok" />
                  <Line yAxisId="roi" type="monotone" dataKey="roi" stroke="#fbbf24" strokeWidth={2} dot={false} name="ROI %" />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* B12 — Daily ad spend table, per channel (mirrors B6's shape) */}
          <div className="h-full">
            <ChartCard title="Ad Spend — Last 60d (table)">
              <div className="max-h-[260px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[#0b0d14]">
                    <tr className="border-b border-white/10">
                      <th className="text-left text-gray-400 py-1 px-2">Date</th>
                      <th className="text-right text-gray-400 py-1 px-2">FB</th>
                      <th className="text-right text-gray-400 py-1 px-2">Goog</th>
                      {adsHasTiktok && <th className="text-right text-gray-400 py-1 px-2">TT</th>}
                      <th className="text-right text-gray-400 py-1 px-2">Total</th>
                      <th className="text-right text-gray-400 py-1 px-2">ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...(ads.dailySpend || [])].reverse().map((d: any, i: number) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="text-gray-300 py-1 px-2 font-mono">{d.date}</td>
                        <td className="text-right text-gray-400 py-1 px-2 font-mono tabular-nums">{fmtUSD0(d.facebook)}</td>
                        <td className="text-right text-gray-400 py-1 px-2 font-mono tabular-nums">{fmtUSD0(d.google)}</td>
                        {adsHasTiktok && <td className="text-right text-gray-400 py-1 px-2 font-mono tabular-nums">{fmtUSD0(d.tiktok)}</td>}
                        <td className="text-right text-amber-400 py-1 px-2 font-mono tabular-nums">{fmtUSD2(d.total || 0)}</td>
                        <td className={`text-right py-1 px-2 font-mono tabular-nums ${(d.roi || 0) >= 200 ? 'text-emerald-400' : (d.roi || 0) >= 100 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {(d.roi || 0).toFixed(0)}%
                        </td>
                      </tr>
                    ))}
                    {(ads.dailySpend || []).length === 0 && (
                      <tr><td colSpan={adsHasTiktok ? 6 : 5} className="text-gray-500 text-center py-6">No ad spend data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          </div>

          {/* B13 — Monthly ad spend by channel (from Oct 2025) */}
          <div className="h-full">
            <ChartCard title="Ad Spend — Monthly 12mo (by channel)">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={ads.monthlySpend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={9} tickLine={false} tickFormatter={(v: string) => (v || '').slice(5)} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={40} tickFormatter={(v: number) => `$${formatNumber(v)}`} />
                  <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload || {};
                    return (
                      <div style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#fff' }}>
                        <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
                        <p style={{ color: '#1877F2' }}>Facebook: {fmtUSD2(d.facebook || 0)}</p>
                        <p style={{ color: '#EA4335' }}>Google: {fmtUSD2(d.google || 0)}</p>
                        {(d.tiktok || 0) > 0 && <p style={{ color: '#22d3ee' }}>TikTok: {fmtUSD2(d.tiktok)}</p>}
                        <p style={{ fontWeight: 700, marginTop: 4, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 4 }}>Total spend: {fmtUSD2(d.total || 0)}</p>
                        <p style={{ color: '#4ade80' }}>Revenue: {fmtUSD2(d.revenue || 0)}</p>
                        <p style={{ color: (d.roi || 0) >= 100 ? '#fbbf24' : '#f87171', fontWeight: 700 }}>ROI: {(d.roi || 0).toFixed(0)}%</p>
                        <p style={{ color: '#94a3b8', marginTop: 4 }}>
                          {(d.installs || 0).toLocaleString()} installs · CPI ${(d.cpi || 0).toFixed(3)}
                        </p>
                      </div>
                    );
                  }} />
                  <Bar dataKey="facebook" stackId="s" fill="#1877F2" name="Facebook" />
                  <Bar dataKey="google" stackId="s" fill="#EA4335" name="Google" />
                  <Bar dataKey="tiktok" stackId="s" fill="#22d3ee" name="TikTok" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* B14 — Spend headline: per-channel split, run-rate, ROI */}
          <div className="h-full">
            <ChartCard title="Ad Spend — Channels & Run Rate">
              <div className="p-1 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  {/* Labelled with the actual date, not "Today": the nightly
                      compute writes yesterday's row, so anchoring on today
                      would read $0 all day. */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-2">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                      {ads.latestDate ? `Latest · ${ads.latestDate.slice(5)}` : 'Latest'}
                    </p>
                    <p className="text-xl font-bold text-amber-400 leading-tight">{fmtUSD2(ads.latestSpend || 0)}</p>
                  </div>
                  {/* Today is fetched separately every 30 min and is PARTIAL —
                      shown apart from the complete series so it never gets
                      averaged in as if the day were finished. */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-2">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                      {ads.todayPartial ? 'Today · so far' : 'This Month'}
                    </p>
                    <p className="text-xl font-bold text-amber-400 leading-tight">
                      {ads.todayPartial ? fmtUSD2(ads.todayPartial.spend || 0) : fmtUSD(ads.monthSpend || 0)}
                    </p>
                    {ads.todayPartial && (
                      <p className="text-[9px] text-gray-500 leading-tight mt-0.5">
                        partial · {(ads.todayPartial.installs || 0).toLocaleString()} installs
                      </p>
                    )}
                  </div>
                </div>

                <div className="border-t border-white/10 pt-2 space-y-1">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">This month by channel</p>
                  {adsChannels.map((ch) => (
                    <div key={ch.key} className="flex items-center justify-between text-xs gap-2">
                      <span className="flex items-center gap-1.5 flex-1">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: ch.color }} />
                        <span className="text-gray-400">{ch.label}</span>
                      </span>
                      <span className="text-gray-500 tabular-nums">{ch.pct}%</span>
                      <span className="text-gray-200 font-mono tabular-nums">{fmtUSD(ch.value)}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-white/10 pt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Avg/day 7d</span>
                    <span className="text-gray-200 font-mono tabular-nums">{fmtUSD2(ads.avgDaily7d || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Avg/day 30d</span>
                    <span className="text-gray-200 font-mono tabular-nums">{fmtUSD2(ads.avgDaily30d || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Spend 7d</span>
                    <span className="text-gray-200 font-mono tabular-nums">{fmtUSD(ads.spend7d || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Spend 30d</span>
                    <span className="text-gray-200 font-mono tabular-nums">{fmtUSD(ads.spend30d || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">ROI 7d</span>
                    <span className={`font-mono tabular-nums ${(ads.roi7d || 0) >= 200 ? 'text-emerald-400' : (ads.roi7d || 0) >= 100 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {(ads.roi7d || 0).toFixed(0)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">ROI 30d</span>
                    <span className={`font-mono tabular-nums ${(ads.roi30d || 0) >= 200 ? 'text-emerald-400' : (ads.roi30d || 0) >= 100 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {(ads.roi30d || 0).toFixed(0)}%
                    </span>
                  </div>
                </div>

                {/* Health strip. Without this the page cannot distinguish
                    "we spent $0" from "the fetch broke" — each network's
                    fetchAndPopulate swallows its own errors and still marks
                    the day complete. */}
                {(() => {
                  const h = ads.dataHealth;
                  if (!h) return null;
                  const badChannels = Object.entries(h.channels || {})
                    .filter(([, v]: any) => v?.suspiciousZero)
                    .map(([k]) => k);
                  const broken = h.isStale || badChannels.length > 0;
                  return (
                    <div className={`border-t pt-2 ${broken ? 'border-red-500/30' : 'border-white/10'}`}>
                      {h.isStale && (
                        <p className="text-[11px] text-red-400 font-semibold leading-snug">
                          ⚠ STALE — newest data is {h.daysBehind} days old
                          (expected {h.expectedLagDays}). The 02:00 UTC rollover
                          may not be running.
                        </p>
                      )}
                      {badChannels.map((c) => (
                        <p key={c} className="text-[11px] text-red-400 font-semibold leading-snug">
                          ⚠ {c === 'facebook' ? 'Facebook' : 'Google'} reported $0
                          on {ads.latestDate} after spending on{' '}
                          {h.channels[c].priorDaysWithSpend}/7 prior days — likely
                          a failed fetch, not a real pause. Check app-secrets.
                        </p>
                      ))}
                      {/* Geo-exclusion drift. An ad set left on an older
                          exclusion list is how Kenya ran for weeks at 14% ROI,
                          so it gets the same prominence as a broken fetch. */}
                      {(ads.targetingAudit || []).filter((a: any) => (a.driftActive || 0) > 0).map((a: any) => (
                        <p key={a.account} className="text-[11px] text-amber-400 font-semibold leading-snug">
                          ⚠ {a.account}: {a.driftActive ?? a.driftCount} ACTIVE ad sets are
                          behind the {a.referenceCount}-country exclusion list ({a.driftCount} incl. paused).
                        </p>
                      ))}
                      {!broken && (
                        <p className="text-[10px] text-emerald-400/80 leading-snug">
                          ✓ Pipeline healthy · complete through {ads.latestDate}
                          {ads.todayPartial ? ' · today live' : ''} · nightly {h.sourceUpdatedDaily}
                        </p>
                      )}
                    </div>
                  );
                })()}

                <p className="text-[10px] text-gray-600 leading-snug border-t border-white/10 pt-2">
                  Ad spend is fetched <strong>once a day at 02:00 UTC</strong> for
                  the previous day — this panel is never same-day. Figures are
                  delivery cost as the networks report it, not the invoice: Meta
                  bills at thresholds and adds GST/VAT on the SGD/INR accounts, so
                  bank charges run ~6-9% higher.
                </p>
              </div>
            </ChartCard>
          </div>
        </div>

        {/* B15 — This month by country: spend vs revenue vs profit.
            B10 above answers "where did revenue come from"; this answers
            "what did we pay for it", which is the half that was missing. */}
        <div className="mt-3 sm:mt-4">
          <ChartCard title="This Month by Country — Spend vs Revenue">
            <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#0b0d14]">
                  <tr className="border-b border-white/10">
                    <th className="text-left text-gray-400 py-1.5 px-2">Country</th>
                    <th className="text-right text-gray-400 py-1.5 px-2">Facebook</th>
                    <th className="text-right text-gray-400 py-1.5 px-2">Google</th>
                    {adsHasTiktok && <th className="text-right text-gray-400 py-1.5 px-2">TikTok</th>}
                    <th className="text-right text-gray-400 py-1.5 px-2">Spend</th>
                    <th className="text-right text-gray-400 py-1.5 px-2">Revenue</th>
                    <th className="text-right text-gray-400 py-1.5 px-2">Profit</th>
                    <th className="text-right text-gray-400 py-1.5 px-2">ROI</th>
                    <th className="text-right text-gray-400 py-1.5 px-2">Installs</th>
                    <th className="text-right text-gray-400 py-1.5 px-2">CPI</th>
                  </tr>
                </thead>
                <tbody>
                  {(ads.spendByCountry || []).map((c: any, i: number) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                      <td className="text-gray-300 py-1 px-2">{c.country}</td>
                      <td className="text-right text-gray-500 py-1 px-2 font-mono tabular-nums">{fmtUSD0(c.facebook)}</td>
                      <td className="text-right text-gray-500 py-1 px-2 font-mono tabular-nums">{fmtUSD0(c.google)}</td>
                      {adsHasTiktok && <td className="text-right text-gray-500 py-1 px-2 font-mono tabular-nums">{fmtUSD0(c.tiktok)}</td>}
                      <td className="text-right text-amber-400 py-1 px-2 font-mono tabular-nums">{fmtUSD2(c.spend || 0)}</td>
                      <td className="text-right text-emerald-400 py-1 px-2 font-mono tabular-nums">{fmtUSD2(c.revenue || 0)}</td>
                      <td className={`text-right py-1 px-2 font-mono tabular-nums ${(c.profit || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {(c.profit || 0) < 0 ? '−' : ''}{fmtUSD2(Math.abs(c.profit || 0))}
                      </td>
                      <td className={`text-right py-1 px-2 font-mono tabular-nums ${(c.roi || 0) >= 200 ? 'text-emerald-400' : (c.roi || 0) >= 100 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {(c.roi || 0).toFixed(0)}%
                      </td>
                      <td className="text-right text-gray-400 py-1 px-2 font-mono tabular-nums">{(c.installs || 0).toLocaleString()}</td>
                      <td className="text-right text-gray-500 py-1 px-2 font-mono tabular-nums">
                        {(c.installs || 0) > 0 ? `$${(c.spend / c.installs).toFixed(3)}` : '—'}
                      </td>
                    </tr>
                  ))}
                  {(ads.spendByCountry || []).length === 0 && (
                    <tr><td colSpan={adsHasTiktok ? 10 : 9} className="text-gray-500 text-center py-6">No ad spend this month</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </div>

        {/* B16 — campaign and ad level, last 7 complete days.
            The country view answers "which markets"; this answers "which
            campaign and which creative", which previously had to be pulled
            by hand every time. */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4">
          {[
            { title: 'Top Campaigns', rows: ads.topCampaigns || [], showCampaign: false },
            { title: 'Top Ads', rows: ads.topAds || [], showCampaign: true },
          ].map((blk) => (
            <ChartCard key={blk.title} title={`${blk.title} — last 7d`}>
              <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[#0b0d14]">
                    <tr className="border-b border-white/10">
                      <th className="text-left text-gray-400 py-1.5 px-2">Name</th>
                      <th className="text-left text-gray-400 py-1.5 px-2">Acct</th>
                      <th className="text-right text-gray-400 py-1.5 px-2">Spend</th>
                      <th className="text-right text-gray-400 py-1.5 px-2">Inst</th>
                      <th className="text-right text-gray-400 py-1.5 px-2">CPI</th>
                      <th className="text-right text-gray-400 py-1.5 px-2">Purch</th>
                      <th className="text-right text-gray-400 py-1.5 px-2">$/Purch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blk.rows.map((r: any, i: number) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                        <td className="text-gray-300 py-1 px-2 max-w-[160px] truncate" title={blk.showCampaign ? `${r.campaign || ''} › ${r.name || ''}` : (r.name || '')}>
                          {r.name || '—'}
                          {blk.showCampaign && r.campaign && (
                            <span className="text-gray-600"> · {r.campaign}</span>
                          )}
                        </td>
                        <td className="text-gray-500 py-1 px-2">{r.account}</td>
                        <td className="text-right text-amber-400 py-1 px-2 font-mono tabular-nums">{fmtUSD2(r.spend || 0)}</td>
                        <td className="text-right text-gray-400 py-1 px-2 font-mono tabular-nums">{(r.installs || 0).toLocaleString()}</td>
                        <td className="text-right text-gray-500 py-1 px-2 font-mono tabular-nums">{(r.cpi || 0) > 0 ? `$${(r.cpi).toFixed(3)}` : '—'}</td>
                        <td className="text-right text-gray-400 py-1 px-2 font-mono tabular-nums">{r.purchases || 0}</td>
                        <td className={`text-right py-1 px-2 font-mono tabular-nums ${(r.costPerPurchase || 0) > 0 && r.costPerPurchase < 20 ? 'text-emerald-400' : (r.costPerPurchase || 0) > 0 ? 'text-yellow-400' : 'text-gray-600'}`}>
                          {(r.costPerPurchase || 0) > 0 ? fmtUSD2(r.costPerPurchase) : '—'}
                        </td>
                      </tr>
                    ))}
                    {blk.rows.length === 0 && (
                      <tr><td colSpan={7} className="text-gray-500 text-center py-6">
                        No entity data yet — populated by the nightly ingestion.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          ))}
        </div>
      </section>

      {/* ============ C. VIDEOS ============ */}
      <section>
        <h2 className="text-lg font-bold text-white mb-2">C. Videos</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-3 sm:gap-4">
          {/* C1 — Total + all statuses (highlighted) */}
          <div className="xl:col-span-2 bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col h-full">
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Total Videos</p>
            <p className="text-3xl font-bold text-white mt-1 leading-tight">
              {(videosOverview?.totalVideos || 0).toLocaleString()}
            </p>
            <div className="mt-3 space-y-1 border-t border-white/10 pt-3 text-xs">
              {sortedStatusAlpha.map((s: any, i: number) => {
                const name = String(s.status).toLowerCase();
                const color =
                  name === 'review required' || name === 'review_required' ? 'text-red-400'
                  : name === 'under review' || name === 'under_review' ? 'text-yellow-400'
                  : name === 'upload' || name === 'uploaded' ? 'text-sky-400'
                  : 'text-gray-300';
                return (
                  <div key={i} className="flex justify-between items-center">
                    <span className={color}>{s.status}</span>
                    <span className={`${color} font-mono`}>{(s.count || 0).toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* C2 — Monthly videos + creators + avg */}
          <div className="xl:col-span-4 h-full">
            <ChartCard title="Monthly Videos vs Creators (from Jul 2025)">
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={videosMonthlyMerged}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} />
                  <YAxis yAxisId="left" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} />
                  <YAxis yAxisId="right" orientation="right" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={30} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: any, name: any) => [formatNumber(v), name]} />
                  <Bar yAxisId="left" dataKey="videos" fill="#F42026" name="Videos" radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="left" dataKey="creators" fill="#FF9C15" name="Creators" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="avgPerUser" stroke="#c084fc" strokeWidth={2} dot={{ r: 2 }} name="Avg/Creator" />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* C3 — Daily 30d */}
          <div className="xl:col-span-3 h-full">
            <ChartCard title={`Daily Uploads (${win.label})`}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={videosDaily30}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" fill="#F42026" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* C4 — Hourly 48h */}
          <div className="xl:col-span-3 h-full">
            <ChartCard title="Hourly Uploads (48h)">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={videosHourly48}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={8} tickLine={false} interval="preserveStartEnd" />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" fill="#fb923c" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </div>
      </section>

      {/* ============ D. INTERACTIONS ============ */}
      <section>
        <h2 className="text-lg font-bold text-white mb-2">D. Interactions</h2>

        {/* Row 1 — Daily (30d) interactions: 5 cards @ xl:col-span-2.4 via span-3 grid-cols-15 trick.
            We use md:grid-cols-2 xl:grid-cols-5 so each row of 5 cards spreads evenly. */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 sm:gap-4">
          {[
            { key: 'watches', title: `Watches — Daily (${win.label})`, color: '#FF9C15', data: interactionsDaily30.watches },
            { key: 'likes', title: `Likes — Daily (${win.label})`, color: '#F42026', data: interactionsDaily30.likes },
            { key: 'comments', title: `Comments — Daily (${win.label})`, color: '#fb923c', data: interactionsDaily30.comments },
            { key: 'shares', title: `Shares — Daily (${win.label})`, color: '#38bdf8', data: interactionsDaily30.shares },
            { key: 'saves', title: `Saves — Daily (${win.label})`, color: '#a78bfa', data: interactionsDaily30.saves },
          ].map((s) => (
            <div key={s.key} className="h-full">
              <ChartCard title={s.title}>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={s.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} tickFormatter={(v: number) => formatNumber(v)} />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(v: any) => [(v || 0).toLocaleString(), 'Count']}
                      labelFormatter={(_l: any, payload: any) => payload?.[0]?.payload?.rawDate || ''}
                    />
                    <Bar dataKey="count" fill={s.color} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          ))}
        </div>

        {/* Row 2 — Hourly (48h) interactions */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 sm:gap-4 mt-3 sm:mt-4">
          {[
            { key: 'watches', title: 'Watches — Hourly 48h', color: '#FF9C15', data: interactionsHourly48.watches },
            { key: 'likes', title: 'Likes — Hourly 48h', color: '#F42026', data: interactionsHourly48.likes },
            { key: 'comments', title: 'Comments — Hourly 48h', color: '#fb923c', data: interactionsHourly48.comments },
            { key: 'shares', title: 'Shares — Hourly 48h', color: '#38bdf8', data: interactionsHourly48.shares },
            { key: 'saves', title: 'Saves — Hourly 48h', color: '#a78bfa', data: interactionsHourly48.saves },
          ].map((s) => (
            <div key={s.key} className="h-full">
              <ChartCard title={s.title}>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={s.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="label" stroke="#64748b" fontSize={8} tickLine={false} interval="preserveStartEnd" />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} tickFormatter={(v: number) => formatNumber(v)} />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(v: any) => [(v || 0).toLocaleString(), 'Count']}
                    />
                    <Bar dataKey="count" fill={s.color} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          ))}
        </div>

        {/* Row 3 — Minute-level watches (health check) + message types (30d daily) */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 sm:gap-4 mt-3 sm:mt-4">
          <div className="h-full">
            <ChartCard title="Watches — Minutely 5h (health)">
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={watchesMinutely}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={8} tickLine={false} interval="preserveStartEnd" />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} tickFormatter={(v: number) => formatNumber(v)} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: any) => [(v || 0).toLocaleString(), 'Count']}
                    labelFormatter={(l: any, payload: any) => {
                      const p = payload?.[0]?.payload;
                      return p ? `${p.date} ${l}` : String(l ?? '');
                    }}
                  />
                  <Bar dataKey="count" fill="#FF9C15" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
          {[
            { key: 'dm', title: `DM Messages — Daily (${win.label})`, color: '#38bdf8', data: messagesDaily30.dm },
            { key: 'group', title: `Group Messages — Daily (${win.label})`, color: '#a78bfa', data: messagesDaily30.group },
            { key: 'hub', title: `Hub Messages — Daily (${win.label})`, color: '#34d399', data: messagesDaily30.hub },
            { key: 'oneToOne', title: `One-to-One — Daily (${win.label})`, color: '#fbbf24', data: messagesDaily30.oneToOne },
          ].map((s) => (
            <div key={s.key} className="h-full">
              <ChartCard title={s.title}>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={s.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} tickFormatter={(v: number) => formatNumber(v)} />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(v: any) => [(v || 0).toLocaleString(), 'Count']}
                      labelFormatter={(_l: any, payload: any) => payload?.[0]?.payload?.rawDate || ''}
                    />
                    <Bar dataKey="count" fill={s.color} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          ))}
        </div>
      </section>

      {/* ============ E. SIGNUPS ============ */}
      <section>
        <h2 className="text-lg font-bold text-white mb-2">E. SignUps</h2>

        {/* Row 1 — Daily 30d by source */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 sm:gap-4">
          {[
            { key: 'google', title: `Google — Daily (${win.label})`, color: '#4ade80', data: signupsByTypeDaily30.google },
            { key: 'facebook', title: `Facebook — Daily (${win.label})`, color: '#38bdf8', data: signupsByTypeDaily30.facebook },
            { key: 'phone', title: `Phone — Daily (${win.label})`, color: '#FF9C15', data: signupsByTypeDaily30.phone },
            { key: 'email', title: `Email — Daily (${win.label})`, color: '#a78bfa', data: signupsByTypeDaily30.email },
            { key: 'appleOther', title: `Apple & Other — Daily (${win.label})`, color: '#f87171', data: signupsByTypeDaily30.appleOther },
          ].map((s) => (
            <div key={s.key} className="h-full">
              <ChartCard title={s.title}>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={s.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} tickFormatter={(v: number) => formatNumber(v)} />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(v: any) => [(v || 0).toLocaleString(), 'Signups']}
                      labelFormatter={(_l: any, payload: any) => payload?.[0]?.payload?.rawDate || ''}
                    />
                    <Bar dataKey="count" fill={s.color} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          ))}
        </div>

        {/* Row 2 — Hourly 48h by source */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 sm:gap-4 mt-3 sm:mt-4">
          {[
            { key: 'google', title: 'Google — Hourly 48h', color: '#4ade80', data: signupsByTypeHourly48.google },
            { key: 'facebook', title: 'Facebook — Hourly 48h', color: '#38bdf8', data: signupsByTypeHourly48.facebook },
            { key: 'phone', title: 'Phone — Hourly 48h', color: '#FF9C15', data: signupsByTypeHourly48.phone },
            { key: 'email', title: 'Email — Hourly 48h', color: '#a78bfa', data: signupsByTypeHourly48.email },
            { key: 'appleOther', title: 'Apple & Other — Hourly 48h', color: '#f87171', data: signupsByTypeHourly48.appleOther },
          ].map((s) => (
            <div key={s.key} className="h-full">
              <ChartCard title={s.title}>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={s.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="label" stroke="#64748b" fontSize={8} tickLine={false} interval="preserveStartEnd" />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} tickFormatter={(v: number) => formatNumber(v)} />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(v: any) => [(v || 0).toLocaleString(), 'Signups']}
                    />
                    <Bar dataKey="count" fill={s.color} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          ))}
        </div>
      </section>

      {/* ============ F. RETENTION ============ */}
      <section>
        <h2 className="text-lg font-bold text-white mb-2">F. Retention</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 sm:gap-4">
          {/* DAU 30d */}
          <div className="h-full">
            <ChartCard title={`DAU — Daily (${win.label})`}>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={retentionDau30}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} tickFormatter={(v: number) => formatNumber(v)} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: any) => [(v || 0).toLocaleString(), 'Active Users']}
                    labelFormatter={(_l: any, payload: any) => payload?.[0]?.payload?.rawDate || ''}
                  />
                  <Bar dataKey="dau" fill="#4ade80" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* MAU 12m */}
          <div className="h-full">
            <ChartCard title="MAU — Monthly 12m">
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={retentionMau12}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} tickFormatter={(v: number) => formatNumber(v)} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: any) => [(v || 0).toLocaleString(), 'Active Users']}
                  />
                  <Bar dataKey="mau" fill="#38bdf8" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* D1 Retention */}
          <div className="h-full">
            <ChartCard title="D1 Retention (by cohort)">
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={retentionCohorts}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: any, _n: any, ctx: any) => {
                      if (v == null) return ['—', 'D1 Retention'];
                      const size = ctx?.payload?.cohortSize || 0;
                      return [`${v}% of ${size.toLocaleString()}`, 'D1 Retention'];
                    }}
                    labelFormatter={(_l: any, payload: any) => payload?.[0]?.payload?.rawDate || ''}
                  />
                  <Bar dataKey="d1Rate" fill="#FF9C15" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* D7 Retention */}
          <div className="h-full">
            <ChartCard title="D7 Retention (by cohort)">
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={retentionCohorts}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: any, _n: any, ctx: any) => {
                      if (v == null) return ['—', 'D7 Retention'];
                      const size = ctx?.payload?.cohortSize || 0;
                      return [`${v}% of ${size.toLocaleString()}`, 'D7 Retention'];
                    }}
                    labelFormatter={(_l: any, payload: any) => payload?.[0]?.payload?.rawDate || ''}
                  />
                  <Bar dataKey="d7Rate" fill="#F42026" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* D30 Retention */}
          <div className="h-full">
            <ChartCard title="D30 Retention (by cohort)">
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={retentionCohorts}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} width={36} domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: any, _n: any, ctx: any) => {
                      if (v == null) return ['—', 'D30 Retention'];
                      const size = ctx?.payload?.cohortSize || 0;
                      return [`${v}% of ${size.toLocaleString()}`, 'D30 Retention'];
                    }}
                    labelFormatter={(_l: any, payload: any) => payload?.[0]?.payload?.rawDate || ''}
                  />
                  <Bar dataKey="d30Rate" fill="#a78bfa" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeTab;
